import { Form } from "@inertiajs/react"
import { useId, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { usersPath } from "@/routes"

/**
 * OKS-95 — Danger Zone card on `/settings/profile`.
 *
 * Server-resolved props (from `Settings::ProfilesController#show` via
 * `Users::Deletability`) gate the destructive flow:
 *
 *  - `canDelete: false` → disabled trigger + localized reason banner;
 *    the AlertDialog is never rendered.
 *  - `canDelete: true`  → trigger opens an AlertDialog containing the
 *    Inertia DELETE form with password + typed-email confirmation.
 *
 * Typed-email check is client-side only (server's authoritative gate
 * is `password_challenge`). Match uses `.trim().toLowerCase()` on both
 * sides to avoid CapsLock fatigue on a high-friction destructive
 * action.
 *
 * Crew hard rules honored: shadcn AlertDialog (not Dialog), Inertia
 * `<Form>` (not RHF), `usersPath()` from js-routes, no localStorage.
 */
export type DeleteUserBlockReasonKey =
  | "settings.delete_account.block.last_admin"
  | "settings.delete_account.block.agency_has_assignments"
  | "settings.delete_account.block.last_brand_admin"
  | "settings.delete_account.block.audit_trail_holdings"

export interface DeleteUserProps {
  canDelete: boolean
  blockReasonKey: DeleteUserBlockReasonKey | null
  userEmail: string
}

export default function DeleteUser({
  canDelete,
  blockReasonKey,
  userEmail,
}: DeleteUserProps) {
  const { t } = useTranslation()
  const passwordInput = useRef<HTMLInputElement>(null)
  const [password, setPassword] = useState("")
  const [typedEmail, setTypedEmail] = useState("")
  const emailHelperId = useId()

  const normalizedTyped = typedEmail.trim().toLowerCase()
  const normalizedTarget = userEmail.trim().toLowerCase()
  const canSubmit = normalizedTyped === normalizedTarget && password.length > 0

  return (
    <div className="space-y-6" data-testid="delete-user-card">
      <HeadingSmall
        title={t("settings.delete_account.title")}
        description={t("settings.delete_account.description")}
      />
      <div className="space-y-4 rounded-lg border border-red-100 bg-red-50 p-4 dark:border-red-200/10 dark:bg-red-700/10">
        <div className="relative space-y-0.5 text-red-600 dark:text-red-100">
          <p className="font-medium">
            {t("settings.delete_account.warning_title")}
          </p>
          <p className="text-sm">{t("settings.delete_account.warning_body")}</p>
        </div>

        {canDelete ? (
          <DeleteDialog
            userEmail={userEmail}
            password={password}
            setPassword={setPassword}
            typedEmail={typedEmail}
            setTypedEmail={setTypedEmail}
            canSubmit={canSubmit}
            passwordInputRef={passwordInput}
            emailHelperId={emailHelperId}
          />
        ) : (
          <BlockedTrigger blockReasonKey={blockReasonKey} />
        )}
      </div>
    </div>
  )
}

function BlockedTrigger({
  blockReasonKey,
}: {
  blockReasonKey: DeleteUserBlockReasonKey | null
}) {
  const { t } = useTranslation()

  return (
    <div className="space-y-2">
      <Button
        variant="destructive"
        aria-disabled="true"
        className="cursor-not-allowed opacity-60"
        onClick={(e) => e.preventDefault()}
        data-testid="delete-user-trigger-blocked"
      >
        {t("settings.delete_account.trigger")}
      </Button>
      {blockReasonKey ? (
        <p
          className="text-sm text-red-700 dark:text-red-200"
          data-testid="delete-user-block-reason"
        >
          {t(blockReasonKey)}
        </p>
      ) : null}
    </div>
  )
}

function DeleteDialog({
  userEmail,
  password,
  setPassword,
  typedEmail,
  setTypedEmail,
  canSubmit,
  passwordInputRef,
  emailHelperId,
}: {
  userEmail: string
  password: string
  setPassword: (value: string) => void
  typedEmail: string
  setTypedEmail: (value: string) => void
  canSubmit: boolean
  passwordInputRef: React.RefObject<HTMLInputElement | null>
  emailHelperId: string
}) {
  const { t } = useTranslation()

  return (
    <AlertDialog
      onOpenChange={(open) => {
        if (!open) {
          setPassword("")
          setTypedEmail("")
        }
      }}
    >
      <AlertDialogTrigger
        render={
          <Button variant="destructive" data-testid="delete-user-trigger">
            {t("settings.delete_account.trigger")}
          </Button>
        }
      />
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("settings.delete_account.dialog.title")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("settings.delete_account.dialog.description")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Form
          method="delete"
          action={usersPath()}
          options={{ preserveScroll: true }}
          onError={() => passwordInputRef.current?.focus()}
          resetOnSuccess
          className="space-y-4"
          data-testid="delete-user-form"
        >
          {({ processing, errors }) => (
            <>
              <div className="grid gap-2">
                <Label htmlFor="password_challenge">
                  {t("settings.delete_account.dialog.password_label")}
                </Label>
                <Input
                  id="password_challenge"
                  type="password"
                  name="password_challenge"
                  ref={passwordInputRef}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t(
                    "settings.delete_account.dialog.password_placeholder",
                  )}
                  autoComplete="current-password"
                  aria-describedby="password_challenge_error"
                  data-testid="delete-user-password"
                />
                <InputError
                  id="password_challenge_error"
                  messages={errors.password_challenge}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="delete_user_email_confirm">
                  {t("settings.delete_account.dialog.email_label", {
                    email: userEmail,
                  })}
                </Label>
                <Input
                  id="delete_user_email_confirm"
                  type="email"
                  name="email_confirm"
                  value={typedEmail}
                  onChange={(e) => setTypedEmail(e.target.value)}
                  placeholder={t(
                    "settings.delete_account.dialog.email_placeholder",
                  )}
                  autoComplete="off"
                  aria-describedby={emailHelperId}
                  data-testid="delete-user-email-confirm"
                />
                <span id={emailHelperId} className="sr-only">
                  {t("settings.delete_account.dialog.email_helper", {
                    email: userEmail,
                  })}
                </span>
              </div>

              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-user-cancel">
                  {t("settings.delete_account.dialog.cancel")}
                </AlertDialogCancel>
                <Button
                  variant="destructive"
                  type="submit"
                  disabled={!canSubmit || processing}
                  data-testid="delete-user-submit"
                >
                  {t("settings.delete_account.dialog.submit")}
                </Button>
              </AlertDialogFooter>
            </>
          )}
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  )
}
